import { alpha, createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0E7BFF',
      light: '#4EA3FF',
      dark: '#005DCC',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FF6E4D',
      light: '#FF8D74',
      dark: '#E45030',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#00A5FF',
    },
    success: {
      main: '#0CA678',
    },
    warning: {
      main: '#F08C00',
    },
    error: {
      main: '#E03131',
    },
    background: {
      default: '#F5F7FB',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F1D37',
      secondary: '#4E607F',
    },
    divider: alpha('#19315A', 0.15),
  },
  shape: {
    borderRadius: 16,
  },
  typography: {
    fontFamily: [
      'Manrope',
      'Avenir Next',
      'Segoe UI',
      'sans-serif',
    ].join(','),
    h1: {
      fontFamily: 'Plus Jakarta Sans, Avenir Next, Segoe UI, sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    h2: {
      fontFamily: 'Plus Jakarta Sans, Avenir Next, Segoe UI, sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    h3: {
      fontFamily: 'Plus Jakarta Sans, Avenir Next, Segoe UI, sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontFamily: 'Plus Jakarta Sans, Avenir Next, Segoe UI, sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontFamily: 'Plus Jakarta Sans, Avenir Next, Segoe UI, sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontFamily: 'Plus Jakarta Sans, Avenir Next, Segoe UI, sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    body1: {
      lineHeight: 1.65,
      fontWeight: 500,
    },
    body2: {
      lineHeight: 1.6,
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
      fontWeight: 700,
      letterSpacing: '0.01em',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (themeParam) => ({
        body: {
          background:
            `radial-gradient(960px 560px at -2% -8%, ${alpha(themeParam.palette.info.main, 0.2)} 0%, rgba(0,0,0,0) 58%),
             radial-gradient(760px 460px at 104% 6%, ${alpha(themeParam.palette.secondary.main, 0.18)} 0%, rgba(0,0,0,0) 52%),
             ${themeParam.palette.background.default}`,
        },
        '::selection': {
          backgroundColor: alpha(themeParam.palette.primary.main, 0.26),
        },
        'a:focus-visible, button:focus-visible, [role="button"]:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible': {
          outline: `3px solid ${alpha(themeParam.palette.primary.main, 0.36)}`,
          outlineOffset: 2,
          borderRadius: 10,
        },
      }),
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 18,
          border: `1px solid ${t.palette.divider}`,
          backgroundImage: 'none',
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 18,
          border: `1px solid ${t.palette.divider}`,
          boxShadow: `0px 14px 34px ${alpha('#102040', 0.08)}`,
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 14,
          minHeight: 44,
          padding: '0 18px',
          transition: t.transitions.create(['background-color', 'box-shadow', 'transform'], {
            duration: 160,
          }),
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        }),
        containedPrimary: ({ theme: t }) => ({
          backgroundImage: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.info.main} 100%)`,
          boxShadow: `0px 12px 24px ${alpha(t.palette.primary.main, 0.24)}`,
          '&:hover': {
            boxShadow: `0px 16px 30px ${alpha(t.palette.primary.main, 0.3)}`,
          },
        }),
        containedSecondary: ({ theme: t }) => ({
          backgroundImage: `linear-gradient(135deg, ${t.palette.secondary.main} 0%, #FF9B4B 100%)`,
          boxShadow: `0px 12px 24px ${alpha(t.palette.secondary.main, 0.22)}`,
          '&:hover': {
            boxShadow: `0px 16px 30px ${alpha(t.palette.secondary.main, 0.28)}`,
          },
        }),
        outlined: ({ theme: t }) => ({
          borderColor: alpha(t.palette.text.primary, 0.2),
          '&:hover': {
            borderColor: alpha(t.palette.primary.main, 0.38),
            backgroundColor: alpha(t.palette.primary.main, 0.06),
          },
        }),
      },
    },
    MuiFab: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 16,
          backgroundImage: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.info.main} 100%)`,
          boxShadow: `0px 18px 36px ${alpha(t.palette.primary.main, 0.24)}`,
          color: t.palette.common.white,
          '&:hover': {
            backgroundImage: `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.info.main} 100%)`,
            boxShadow: `0px 20px 44px ${alpha(t.palette.primary.main, 0.3)}`,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 999,
          fontWeight: 700,
          border: `1px solid ${alpha(t.palette.text.primary, 0.12)}`,
          backgroundColor: alpha(t.palette.primary.main, 0.06),
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 13,
          backgroundColor: t.palette.background.paper,
          transition: t.transitions.create(['box-shadow', 'border-color'], { duration: 150 }),
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(t.palette.text.primary, 0.26),
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${alpha(t.palette.primary.main, 0.16)}`,
          },
        }),
        notchedOutline: ({ theme: t }) => ({
          borderColor: alpha(t.palette.text.primary, 0.18),
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme: t }) => ({
          borderRadius: 12,
          padding: '10px 12px',
          backgroundColor: alpha('#0C1A30', 0.94),
          boxShadow: `0px 16px 34px ${alpha('#0C1A30', 0.34)}`,
          fontSize: '0.84rem',
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderColor: t.palette.divider,
        }),
      },
    },
  },
});

export default theme;
